export type PageKind = 'article' | 'landing' | 'form';

// One product per guide cocoon: the backend searches that product's corpus and hands over by CRM callback (loan), e-mail (travel, prévoyance) or not at all (health).
export type GuideChatProduct = 'loan' | 'health' | 'travel' | 'provident';

export const GUIDE_CHAT_PRODUCTS: readonly GuideChatProduct[] = ['loan', 'health', 'travel', 'provident'];

export function isGuideChatProduct(value: unknown): value is GuideChatProduct {
  return typeof value === 'string' && (GUIDE_CHAT_PRODUCTS as readonly string[]).includes(value);
}

// Only the loan CRM takes a callback task: the other products hand over by e-mail, like the site's /contact page.
export function hasCallbackTask(product: GuideChatProduct): boolean {
  return product === 'loan';
}

// Business rule: the health assistant hands nobody over to an adviser, not even by e-mail while it is down.
export function handsOverToAdviser(product: GuideChatProduct): boolean {
  return product !== 'health';
}

export function hasContactForm(product: GuideChatProduct): boolean {
  return handsOverToAdviser(product) && !hasCallbackTask(product);
}

// The panel's product decides the outage form, whatever product the 503 body names.
export function outageProduct(info: { product?: unknown }, panelProduct?: GuideChatProduct | null): GuideChatProduct {
  return panelProduct ?? (isGuideChatProduct(info.product) ? info.product : 'loan');
}

export type FormStep = 'health' | 'borrower' | 'loan' | 'bank' | 'project' | 'identity';

export interface PageContext {
  kind: PageKind;
  url: string;
  title?: string;
  step?: FormStep | null;
  field?: string | null;
  product?: GuideChatProduct;
}

export type OpenerTrigger =
  | 'scroll'
  | 'read_time'
  | 'exit_intent'
  | 'faq_hover'
  | 'landing_time'
  | 'field_idle'
  | 'hesitation'
  | 'risky_step'
  | 'focus_loss'
  | 'inactivity';

export type LoanTypeValue = 'residential' | 'rental' | 'professional';
export type ProjectTypeValue = 'newLoan' | 'switching';
export type ProjectStatusValue = 'project' | 'loan' | 'browsing';
export type CoverageValue = 'death' | 'ptia' | 'ipt' | 'itt' | 'unemployment';

export interface CollectedField<T> {
  value: T;
  isEstimate: boolean;
}

export interface CollectedQuoteData {
  birthDate?: CollectedField<string> | null;
  smoker?: CollectedField<boolean> | null;
  profession?: CollectedField<string> | null;
  hasCoBorrower?: CollectedField<boolean> | null;
  coBirthDate?: CollectedField<string> | null;
  coSmoker?: CollectedField<boolean> | null;
  coProfession?: CollectedField<string> | null;
  loanType?: CollectedField<LoanTypeValue> | null;
  loanAmount?: CollectedField<number> | null;
  remainingMonths?: CollectedField<number> | null;
  rate?: CollectedField<number> | null;
  startDate?: CollectedField<string> | null;
  bank?: CollectedField<string> | null;
  proportionMain?: CollectedField<number> | null;
  proportionCo?: CollectedField<number> | null;
  projectType?: CollectedField<ProjectTypeValue> | null;
  currentInsurer?: CollectedField<string> | null;
  coverage?: CollectedField<CoverageValue[]> | null;
  // Conversation state owned by the backend: sent back untouched on every turn, never applied to the form.
  projectStatus?: CollectedField<ProjectStatusValue> | null;
  projectStatusAt?: CollectedField<number> | null;
  declinedFields?: CollectedField<string[]> | null;
  qualificationAskedAt?: CollectedField<number[]> | null;
  collectionOpenedAt?: CollectedField<number> | null;
}

export type CollectedFieldName = keyof CollectedQuoteData;

export const ESSENTIAL_FIELDS: CollectedFieldName[] = [
  'birthDate',
  'smoker',
  'profession',
  'hasCoBorrower',
  'loanType',
  'loanAmount',
  'remainingMonths',
  'bank',
  'projectType',
];

export interface HistoryTurn {
  question: string;
  answer: string;
  // Kept so the « Sources » list survives a reload or a resume; the backend history only needs question and answer.
  citations?: Citation[];
}

export interface Citation {
  title?: string;
  url?: string;
  sourceType?: string;
  sourceLabel?: string | null;
  [key: string]: unknown;
}

export interface Escalation {
  reason: string;
  label?: string;
  transitionMessage?: string;
}

export interface SubjectNotice {
  message: string;
  url?: string | null;
}

export interface UnavailableResponse {
  unavailable: true;
  message: string;
  faqUrl?: string;
  appointmentUrl?: string;
  // The backend asks for the callback form instead of plain links.
  appointment?: boolean;
  product?: GuideChatProduct;
}

export interface PhoneCta {
  number: string;
  href: string;
  label: string;
}

export type RefusalKind = 'not_in_guide' | 'insurer_not_compared';

// Lets the stats tell a question the visitor wrote from a chip they clicked.
export type QuestionSource = 'typed' | 'suggestion' | 'quick_reply';

export interface AskRequest {
  question: string;
  history?: HistoryTurn[];
  conversationId?: string;
  funnelStage?: string;
  pageContext?: PageContext;
  collected?: CollectedQuoteData;
  // The product the conversation is on; the backend switches it when the question names another one it covers.
  product?: GuideChatProduct;
  questionSource?: QuestionSource;
}

export interface AskResponse {
  conversationId?: string;
  product?: GuideChatProduct;
  productLabel?: string;
  productSwitched?: boolean;
  answer?: string;
  citations?: Citation[];
  subjectNotice?: SubjectNotice | string | null;
  quoteCta?: unknown;
  appointmentCard?: unknown;
  contactCard?: unknown;
  collected?: CollectedQuoteData;
  missingEssential?: CollectedFieldName[];
  nextQuestion?: string | null;
  nextQuestionField?: string | null;
  escalation?: Escalation | null;
  prefillUrl?: string | null;
  conversationClosed?: boolean;
  phoneCta?: PhoneCta | null;
  refusalKind?: RefusalKind | null;
  prefillOffer?: boolean;
  subjectRedirect?: SubjectNotice | null;
  [key: string]: unknown;
}

export interface OpenerRequest {
  conversationId?: string;
  pageContext: PageContext;
  trigger: OpenerTrigger;
}

export interface OpenerResponse {
  message: string;
  suggestions: string[];
  openerId: string;
}

export interface ConversationState {
  conversationId: string;
  history: HistoryTurn[];
  collected: CollectedQuoteData;
  funnelStage?: string | null;
  lastActivityAt?: string | null;
  product?: GuideChatProduct | null;
}

export type ChatEventName =
  | 'chat.opener.shown'
  | 'chat.opener.dismissed'
  | 'chat.opener.accepted'
  | 'chat.widget.opened'
  | 'chat.feedback'
  | 'chat.quote.started'
  | 'chat.form.switched'
  | 'chat.session.resumed';
